/* eslint-disable react/prop-types */
import React from "react";
import { formatCurrency } from "../../../utils/format";
import EmptyValue from "../../../components/EmptyValue";
import { EVITAL_QUOTE_BASE_URL } from "../../constants/evital";
// Reused, not restated: the pharmacy console already derives this, and the rule has a
// subtlety (the genesis transition, NOT statusHistory[0]) that a second copy would lose.
import { salesPerson } from "../../../pharmacy/pages/orders/columns";


const LINK_CLASS = "text-elm-500 underline";
const stop = (event) => event.stopPropagation();

/** The row's server-computed finance block, or an empty object if it was not requested. */
const finance = (row) => row?.row?.original?.finance || {};

/** Right-aligned tabular currency, matching the b2b and TAT columns. */
const money = (value) =>
    value === null || value === undefined ? <EmptyValue /> : <span className="tabular-nums">{formatCurrency(value)}</span>;

/**
 * Extra columns for the Sales orders queue.
 *
 * Every one is `disableSortBy`: these are derived server-side per page, not columns on
 * userOrders, so pillup-api's getOrderBy (classes/Generic/Functions.js) cannot sort by
 * them -- and a header that looks sortable but silently does nothing is worse than one
 * that does not offer.
 */
export function buildSalesOrderColumns() {
    return [
        {
            Header: "Outstanding(₹)",
            id: "outstandingAmount",
            accessor: "finance.outstandingAmount",
            disableSortBy: true,
            Cell: (row) => money(finance(row).outstandingAmount),
        },
        {
            Header: "Discount %",
            id: "discountPercentage",
            accessor: "finance.discountPercentage",
            disableSortBy: true,
            Cell: (row) => {
                const pct = finance(row).discountPercentage;
                return pct === null || pct === undefined ? <EmptyValue /> : <span className="tabular-nums">{pct}%</span>;
            },
        },
        {
            Header: "Evital No",
            id: "evitalOrderNumber",
            accessor: "finance.evitalOrderNumber",
            disableSortBy: true,
            Cell: (row) => {
                const id = finance(row).evitalOrderNumber || row?.row?.original?.inventoryId;
                if (!id) return <EmptyValue />;
                return (
                    <a href={`${EVITAL_QUOTE_BASE_URL}${id}`} target="_blank" rel="noreferrer" onClick={stop} className={LINK_CLASS}>
                        {id}
                    </a>
                );
            },
        },
        {
            Header: "Evital Invoice",
            id: "evitalPrintUrl",
            accessor: "finance.evitalPrintUrl",
            disableSortBy: true,
            Cell: (row) => {
                // Lives on the MEDICINE paymentItem's additionalDetails.printUrl, so it
                // is absent until the order has actually been billed in eVitalRX.
                const url = finance(row).evitalPrintUrl;
                if (!url) return <EmptyValue />;
                return (
                    <a href={url} target="_blank" rel="noreferrer" onClick={stop} className={LINK_CLASS}>
                        Open
                    </a>
                );
            },
        },
        // The four payment columns all read the SAME server-chosen latest payment --
        // greatest COALESCE(paidAt, createdAt), tiebreak id -- so they can never
        // describe four different payments on one row.
        {
            Header: "Payment Gateway",
            id: "paymentGateway",
            accessor: "finance.latestPayment.gatewayName",
            disableSortBy: true,
            Cell: (row) => finance(row).latestPayment?.gatewayName || <EmptyValue />,
        },
        {
            Header: "Payment Method",
            id: "paymentMethod",
            accessor: "finance.latestPayment.paymentMethod",
            disableSortBy: true,
            Cell: (row) => finance(row).latestPayment?.paymentMethod || <EmptyValue />,
        },
        {
            Header: "Payment Source",
            id: "paymentSource",
            accessor: "finance.latestPayment.paymentSource",
            disableSortBy: true,
            Cell: (row) => finance(row).latestPayment?.paymentSource || <EmptyValue />,
        },
        {
            Header: "Payment Status",
            id: "paymentStatus",
            accessor: "finance.latestPayment.status",
            disableSortBy: true,
            // No status filter server-side on purpose: this column must read PENDING on
            // an unpaid order rather than going blank.
            Cell: (row) => finance(row).latestPayment?.status || <EmptyValue />,
        },
        {
            Header: "Sales Person",
            id: "salesPerson",
            accessor: "salesPerson",
            disableSortBy: true,
            Cell: (row) => salesPerson(row) || <EmptyValue />,
        },
    ];
}

export default buildSalesOrderColumns;
